/**
 * Zoom de la carte — niveaux des boutons/touches + molette et pincement.
 *
 * Les boutons et les touches + / − passent d'un niveau fixe au suivant ; la
 * molette et le pincement zooment en continu entre le premier et le dernier
 * niveau, le zoom courant peut donc tomber entre deux niveaux.
 */

export const ZOOM_LEVELS = [0.5, 0.75, 1.0, 1.5, 2.0] as const;
export const ZOOM_MIN = ZOOM_LEVELS[0];
export const ZOOM_MAX = ZOOM_LEVELS[ZOOM_LEVELS.length - 1]!;

/** Premier niveau strictement au-dessus (1) ou en dessous (-1) du zoom courant, null en butée. */
export function nextZoomLevel(levels: readonly number[], current: number, direction: 1 | -1): number | null {
  const EPS = 0.01;
  if (direction === 1) return levels.find((l) => l > current + EPS) ?? null;
  for (let i = levels.length - 1; i >= 0; i--) {
    if (levels[i]! < current - EPS) return levels[i]!;
  }
  return null;
}

interface WheelZoomOptions {
  getZoom: () => number;
  setZoom: (level: number) => void;
  min: number;
  max: number;
  /** false = l'événement est quand même bloqué (pas de zoom navigateur), mais ignoré. */
  isEnabled?: () => boolean;
}

/**
 * Zoom à la molette et au pincement du pavé tactile, sur le canvas.
 * - Chrome, Firefox, Edge : le pincement arrive en `wheel` avec `ctrlKey`
 *   (petits deltas) — bloqué sinon le navigateur zoome toute la page.
 * - Safari : le pincement arrive en `gesturestart`/`gesturechange` (`scale`).
 * Retourne la fonction de nettoyage.
 */
export function attachWheelZoom(canvas: HTMLCanvasElement, opts: WheelZoomOptions): () => void {
  const enabled = () => opts.isEnabled?.() ?? true;
  const apply = (level: number) => {
    const clamped = Math.min(opts.max, Math.max(opts.min, level));
    opts.setZoom(Math.round(clamped * 100) / 100);
  };

  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    if (!enabled()) return;
    let dy = e.deltaY;
    if (e.deltaMode === 1) dy *= 16;        // lignes (Firefox)
    else if (e.deltaMode === 2) dy *= 400;  // pages
    // Un cran de molette ≈ 100 px → ~15 % ; un pincement envoie de petits deltas.
    const sensitivity = e.ctrlKey ? 0.01 : 0.0015;
    apply(opts.getZoom() * Math.exp(-dy * sensitivity));
  };

  let gestureStartZoom = 1;
  const onGestureStart = (e: Event) => {
    e.preventDefault();
    gestureStartZoom = opts.getZoom();
  };
  const onGestureChange = (e: Event) => {
    e.preventDefault();
    if (!enabled()) return;
    const scale = (e as Event & { scale?: number }).scale;
    if (typeof scale === 'number' && scale > 0) apply(gestureStartZoom * scale);
  };

  canvas.addEventListener('wheel', onWheel, { passive: false });
  canvas.addEventListener('gesturestart', onGestureStart, { passive: false });
  canvas.addEventListener('gesturechange', onGestureChange, { passive: false });
  return () => {
    canvas.removeEventListener('wheel', onWheel);
    canvas.removeEventListener('gesturestart', onGestureStart);
    canvas.removeEventListener('gesturechange', onGestureChange);
  };
}
